import { useEffect, useState } from 'react'
import { Button, Modal } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'

import { DocumentViewer } from './DocumentViewer'
import { PdfPager } from './PdfPager'
import { ViewerToolbar } from './ViewerToolbar'
import { useZoomPan } from './useZoomPan'
import {
  getSavedFlip,
  getSavedRotation,
  saveFlip,
  saveRotation,
} from './rotation'
import type { ViewerFile } from './useDisplaySources'

export type FullscreenViewerProps = {
  open: boolean
  onClose: () => void
  historyId: string
  files: ViewerFile[]
  index: number
  page: number
  numPages: number
  onIndexChange: (i: number) => void
  onPageChange: (p: number) => void
  onPdfLoad: (n: number) => void
}

export const FullscreenViewer = ({
  open,
  onClose,
  historyId,
  files,
  index,
  page,
  numPages,
  onIndexChange,
  onPageChange,
  onPdfLoad,
}: FullscreenViewerProps) => {
  const zoomPan = useZoomPan()
  const { reset: resetZoom, setAnimate } = zoomPan
  const [rotate, setRotate] = useState(0)
  const [flipX, setFlipX] = useState(false)
  const current = files[index]
  const height = Math.round(window.innerHeight * 0.8)

  // Resync rotate/zoom whenever the open doc or page changes.
  // resetZoom/setAnimate are stable (useCallback / state setter); current is stable
  // (parent memoizes `files`), so the dep list does not re-run on every render.
  useEffect(() => {
    if (!open || !current) return
    resetZoom()
    setAnimate(false)
    setRotate(
      getSavedRotation(
        historyId,
        current.src,
        current.isPdf ? page : undefined,
      ),
    )
    setFlipX(
      getSavedFlip(historyId, current.src, current.isPdf ? page : undefined),
    )
  }, [open, current, page, historyId, resetZoom, setAnimate])

  const handleRotate = (delta: number) => {
    if (!current) return
    zoomPan.setAnimate(true)
    const next = rotate + delta
    setRotate(next)
    saveRotation(historyId, current.src, next, current.isPdf ? page : undefined)
  }

  const handleFlip = () => {
    if (!current) return
    const next = !flipX
    setFlipX(next)
    saveFlip(historyId, current.src, next, current.isPdf ? page : undefined)
  }

  if (!current) return null

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="60vw"
      centered
      style={{ marginRight: '25%' }}
      zIndex={1050}
      styles={{ body: { padding: 0 } }}
    >
      <DocumentViewer
        src={current.displaySrc}
        status={current.status}
        isPdf={current.isPdf}
        pageNumber={page}
        rotate={rotate}
        flipX={flipX}
        zoomPan={zoomPan}
        height={height}
        background="#2e2e2e"
        onPdfLoad={onPdfLoad}
      />
      {current.isPdf && numPages > 1 && (
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}
        >
          <PdfPager page={page} numPages={numPages} onChange={onPageChange} />
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <ViewerToolbar
          scale={zoomPan.scale}
          onZoom={(d) => zoomPan.setScaleAnimated(zoomPan.scale + d)}
          onRotateLeft={() => handleRotate(-90)}
          onRotateRight={() => handleRotate(90)}
          isFlipped={flipX}
          onFlip={handleFlip}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 8,
        }}
      >
        <Button
          icon={<LeftOutlined />}
          disabled={index <= 0}
          onClick={() => onIndexChange(index - 1)}
        />
        <Button
          icon={<RightOutlined />}
          disabled={index >= files.length - 1}
          onClick={() => onIndexChange(index + 1)}
        />
      </div>
    </Modal>
  )
}
